import { useState, useEffect } from 'react';
import type { Contact, Group } from '@/types';
import { normalizeBrazilianPhone, isMissingBrazilianCountryCode, BRAZIL_DDDS, DEFAULT_DDD } from '@/lib/brazilDDDs';

interface ContactModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Partial<Contact>) => void;
  groups: Group[];
  saving?: boolean;
}

export default function ContactModal({ contact, isOpen, onClose, onSave, groups, saving }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', company_name: '', city: '', state: '', tags: '', notes: '', group_id: '',
  });
  const [ddd, setDdd] = useState(DEFAULT_DDD);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name,
        phone: contact.phone,
        email: contact.email || '',
        company_name: contact.company_name || '',
        city: contact.city || '',
        state: contact.state || '',
        tags: (contact.tags || []).join(', '),
        notes: contact.notes || '',
        group_id: contact.group_id || '',
      });
      // Try to detect DDD from existing phone
      const digits = contact.phone.replace(/\D/g, '');
      if (digits.length >= 10) {
        // Extract DDD: skip country code 55 if present, then first 2 digits = DDD
        const localPart = digits.startsWith('55') ? digits.slice(2) : digits;
        const possibleDDD = localPart.slice(0, 2);
        if (BRAZIL_DDDS.some((d) => d.code === possibleDDD)) {
          setDdd(possibleDDD);
        }
      }
    } else {
      setFormData({ name: '', phone: '', email: '', company_name: '', city: '', state: '', tags: '', notes: '', group_id: '' });
      setDdd(DEFAULT_DDD);
    }
  }, [contact, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedPhone = normalizeBrazilianPhone(formData.phone, ddd);
    onSave({
      ...formData,
      phone: normalizedPhone,
      tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
      group_id: formData.group_id || null,
    });
  };

  const handleAddDDD = () => {
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length >= 8 && digits.length < 12) {
      setFormData({ ...formData, phone: normalizeBrazilianPhone(digits, ddd) });
    }
  };

  const needsDDD = isMissingBrazilianCountryCode(formData.phone);
  const previewPhone = formData.phone ? normalizeBrazilianPhone(formData.phone, ddd) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[560px] mx-4 animate-fadeIn">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
          <h3 className="text-base font-heading font-semibold text-foreground-950">
            {contact ? 'Editar Contato' : 'Novo Contato'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nome completo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="Ex: João Silva"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Telefone *</label>
              <div className="flex items-start gap-2">
                <div className="relative flex-1">
                  <i className="ri-smartphone-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    className="w-full pl-9 pr-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="Ex: 5433442777"
                    required
                    maxLength={15}
                  />
                </div>
                <select
                  value={ddd}
                  onChange={(e) => setDdd(e.target.value)}
                  className="px-2 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer min-w-[80px] flex-shrink-0"
                  title="DDD brasileiro"
                >
                  {BRAZIL_DDDS.map((d) => (
                    <option key={d.code} value={d.code}>{d.code} - {d.state}</option>
                  ))}
                </select>
                {needsDDD && (
                  <button
                    type="button"
                    onClick={handleAddDDD}
                    className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
                    title="Adicionar 55 + DDD ao número"
                  >
                    <i className="ri-add-line" />
                    55{ddd}
                  </button>
                )}
              </div>
              {needsDDD && (
                <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-alert-line" />
                  Número sem código de país. Clique em <strong>55{ddd}</strong> para formatar como 55 (Brasil) + DDD {ddd} + número.
                </p>
              )}
              {!needsDDD && formData.phone && (
                <p className="text-xs text-foreground-400 mt-1.5">
                  Será salvo como: <strong className="text-emerald-600 font-mono">{previewPhone}</strong>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Empresa</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="Nome da empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Grupo</label>
              <select
                value={formData.group_id}
                onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              >
                <option value="">Sem grupo</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Cidade</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="Caxias do Sul"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Estado</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              >
                <option value="">Selecione</option>
                {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map((uf) => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tags (separadas por vírgula)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="vip, ativo, promoção"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Observações</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none h-20"
                placeholder="Notas sobre este contato..."
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-background-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              {contact ? 'Salvar alterações' : 'Criar contato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}